import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { toast } from 'sonner';

import { useReorderCarrierServices } from '@/api/domains/carrier-services';
import type { AvailableMethod, CarrierService, Market } from '@/api/types/services';
import { Button } from '@/components/ui/button';

import { GripDotsIcon } from './components/MethodIcons';
import { MethodRow } from './MethodRow';

type ConfiguredMethod = AvailableMethod & { service: CarrierService };

/** Unconfigured methods shown by default while the market has nothing configured yet. */
const DEFAULT_VISIBLE_METHOD_KEYS = ['locker_24_7', 'courier'];

type Props = {
  market: Market;
  methods: AvailableMethod[];
};

/**
 * Configured methods first (sortable, ordered by display_priority), unconfigured
 * methods below in catalog order. Dragging persists dense 0..n-1 priorities.
 * Unconfigured methods are collapsed behind a "Show More" bar: with nothing
 * configured yet the two core methods (parcel locker + courier) stay visible as
 * a starting point, otherwise only configured methods show by default.
 */
export function MarketMethodList({ market, methods }: Props) {
  const { t } = useTranslation('markets');
  const reorder = useReorderCarrierServices(market.id);
  const [showAll, setShowAll] = useState(false);

  const { configured, unconfigured } = useMemo(() => {
    const withService = methods
      .filter((method): method is ConfiguredMethod => method.service != null)
      .sort(
        (a, b) =>
          a.service.display_priority - b.service.display_priority || a.service.id - b.service.id,
      );
    return {
      configured: withService,
      unconfigured: methods.filter((method) => method.service == null),
    };
  }, [methods]);

  const defaultUnconfigured =
    configured.length > 0
      ? []
      : unconfigured.filter((method) => DEFAULT_VISIBLE_METHOD_KEYS.includes(method.method_key));
  const visibleUnconfigured = showAll ? unconfigured : defaultUnconfigured;
  const collapsedCount = unconfigured.length - defaultUnconfigured.length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const ids = configured.map((method) => method.service.id);
    const from = ids.indexOf(Number(active.id));
    const to = ids.indexOf(Number(over.id));
    if (from < 0 || to < 0 || from === to) return;

    // The atomic endpoint expects the full configured order; index = new priority.
    reorder.mutate(arrayMove(ids, from, to), {
      onError: () => {
        // onSettled invalidation restores server truth; just tell the user.
        toast.error(t('methods.reorderFailed'));
      },
    });
  };

  if (methods.length === 0) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 py-4">
        <p className="text-sm text-muted-foreground">{t('methods.empty')}</p>
        <Button variant="outline" size="sm" asChild>
          <Link to="/settings?tab=general">{t('methods.emptyCta')}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={configured.map((method) => method.service.id)}
          strategy={verticalListSortingStrategy}
        >
          {configured.map((method) => (
            <SortableMethodRow
              key={method.service.id}
              market={market}
              method={method}
              nextDisplayPriority={configured.length}
            />
          ))}
        </SortableContext>
      </DndContext>
      {visibleUnconfigured.map((method) => (
        <MethodRow
          key={method.method_key}
          market={market}
          method={method}
          nextDisplayPriority={configured.length}
        />
      ))}
      {collapsedCount > 0 && (
        <button
          type="button"
          aria-expanded={showAll}
          onClick={() => setShowAll((value) => !value)}
          className="flex h-12 w-full items-center justify-center rounded-[4px] bg-[#f7f7f7] text-sm font-medium text-black transition-colors hover:bg-[#f2f2f2]"
        >
          {showAll ? t('methods.showLess') : t('methods.showMore')}
        </button>
      )}
    </div>
  );
}

function SortableMethodRow({
  market,
  method,
  nextDisplayPriority,
}: {
  market: Market;
  method: ConfiguredMethod;
  nextDisplayPriority: number;
}) {
  const { t } = useTranslation('markets');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: method.service.id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'relative z-10' : undefined}
    >
      <MethodRow
        market={market}
        method={method}
        nextDisplayPriority={nextDisplayPriority}
        isDragging={isDragging}
        dragHandle={
          <button
            type="button"
            aria-label={t('methods.dragHint')}
            className="flex size-8 shrink-0 cursor-grab touch-none items-center justify-center rounded-[8px] border border-[#888] p-0.5 transition-colors hover:bg-muted active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <GripDotsIcon className="size-[15px]" />
          </button>
        }
      />
    </div>
  );
}
